import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Section, Button, Input, Select, Textarea, Badge, Empty } from "@/components/ui/kit";
import { fmtCurrency, fmtDate } from "@/utils/format";
import { Edit2, Save, Trash2, X, Search, Filter, AlertCircle, ChevronDown, Plus } from "lucide-react";

export default function UtilizationView() {
  const { id } = useParams();
  const nav = useNavigate();
  
  const [data, setData] = useState(null);
  const [purposes, setPurposes] = useState([]);
  const [donations, setDonations] = useState([]);
  const [err, setErr] = useState(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // --- Rich Form State (Mirrored from Add) ---
  const dropdownRef = useRef(null);
  const [donationSearchTerm, setDonationSearchTerm] = useState("");
  const [isDonationDropdownOpen, setIsDonationDropdownOpen] = useState(false);
  const [fundingSources, setFundingSources] = useState([]);

  const [bens, setBens] = useState([]);
  const [hasSearchedBens, setHasSearchedBens] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [dynamicSchema, setDynamicSchema] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [dynamicBlocks, setDynamicBlocks] = useState([]);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);
  const [selectedBens, setSelectedBens] = useState([]); 

  const [f, setF] = useState({ 
    sectorId: "", projectId: "", activityId: "",
    utilizationDate: "", utilizedAmount: "", utilizationDescription: "", 
    search: "", benType: "" 
  });

  // Handle clicking outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsDonationDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [uRes, pRes, dRes] = await Promise.all([
        api.get(`/utilizations/${id}`),
        api.get("/donation-purposes"),
        api.get("/donations", { params: { size: 500 } })
      ]);
      
      const utilData = uRes.data.data;
      const allPurps = pRes.data.data;
      
      setData(utilData);
      setPurposes(allPurps);
      setDonations(dRes.data.data.content);
      
      initForm(utilData, allPurps);
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  const initForm = (d, allPurps) => {
    let uDate = d.date;
    if (uDate && uDate.includes("T")) uDate = uDate.split("T")[0];

    let sId = "", pId = "", aId = "";
    const matchedPurpose = allPurps.find(p => p.name === d.purposeName);
    if (matchedPurpose) {
      if (matchedPurpose.type === "ACTIVITY") {
        aId = matchedPurpose.id;
        pId = matchedPurpose.parentId;
        const proj = allPurps.find(x => x.id === pId);
        if (proj) sId = proj.parentId;
      } else if (matchedPurpose.type === "PROJECT") {
        pId = matchedPurpose.id;
        sId = matchedPurpose.parentId;
      } else if (matchedPurpose.type === "SECTOR") {
        sId = matchedPurpose.id;
      }
    }

    setF({
      sectorId: sId, projectId: pId, activityId: aId,
      utilizationDate: uDate || "",
      utilizedAmount: d.amount || "",
      utilizationDescription: d.description || "",
      search: "", benType: ""
    });

    setFundingSources(d.fundingSources?.map(s => ({
      donationId: s.donationId,
      donorName: s.donorName,
      purposeName: "Existing Allocation",
      maxAmount: 99999999, // Backend will validate if they over-allocate
      allocatedAmount: s.allocatedAmount
    })) || []);

    setSelectedBens(d.beneficiaries || []);
  };

  const upd = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));

  // --- Memoized Computations ---
  const sectors = useMemo(() => purposes.filter(p => p.type === "SECTOR"), [purposes]);
  const projects = useMemo(() => purposes.filter(p => p.type === "PROJECT" && (!f.sectorId || String(p.parentId) === String(f.sectorId))), [purposes, f.sectorId]);
  const activities = useMemo(() => purposes.filter(p => p.type === "ACTIVITY" && (!f.projectId || String(p.parentId) === String(f.projectId))), [purposes, f.projectId]);

  const activePurposeId = f.activityId || f.projectId || f.sectorId;
  
  const allowedPurposeNames = useMemo(() => {
    if (!activePurposeId) return [];
    const allowed = [];
    let current = purposes.find(p => String(p.id) === String(activePurposeId));
    while (current) {
      allowed.push(current.name);
      current = purposes.find(p => String(p.id) === String(current.parentId));
    }
    return allowed;
  }, [activePurposeId, purposes]);

  const filteredDonations = useMemo(() => {
    let filtered = donations;
    if (activePurposeId) {
      filtered = filtered.filter(d => !d.purposeName || d.purposeName === "ANY" || allowedPurposeNames.includes(d.purposeName));
    }
    if (donationSearchTerm) {
      const term = donationSearchTerm.toLowerCase();
      filtered = filtered.filter(d => d.donorName?.toLowerCase().includes(term) || d.purposeName?.toLowerCase().includes(term) || String(d.donationId).includes(term));
    }
    const addedIds = fundingSources.map(s => s.donationId);
    return filtered.filter(d => !addedIds.includes(d.donationId));
  }, [donations, activePurposeId, allowedPurposeNames, donationSearchTerm, fundingSources]);

  const totalAllocated = useMemo(() => {
    return fundingSources.reduce((sum, s) => sum + (Number(s.allocatedAmount) || 0), 0);
  }, [fundingSources]);

  // --- Handlers ---
  const addFundingSource = async (d) => {
    try {
      const res = await api.get(`/donations/${d.donationId}/journey`);
      const balance = res.data.data.remainingBalance;
      
      if (balance <= 0) return toast.error("This donation has zero remaining balance.");
      
      setFundingSources(prev => [...prev, {
        donationId: d.donationId, donorName: d.donorName, purposeName: d.purposeName, maxAmount: balance, allocatedAmount: ""
      }]);
      setDonationSearchTerm("");
      setIsDonationDropdownOpen(false);
    } catch (err) { toast.error("Failed to load donation balance"); }
  };

  const updateSourceAmount = (id, amount) => { setFundingSources(prev => prev.map(s => s.donationId === id ? { ...s, allocatedAmount: amount } : s)); };
  const removeSource = (id) => { setFundingSources(prev => prev.filter(s => s.donationId !== id)); };

  // --- Beneficiary Filters Logic ---
  useEffect(() => {
    setBens([]); setHasSearchedBens(false); setDynamicBlocks([]);
    if (f.benType === "STUDENT") {
      setIsLoadingFilters(true);
      api.get("/beneficiaries/filters-schema", { params: { type: "STUDENT" } })
        .then(r => { setDynamicSchema((r.data.data.filters || []).filter(flt => flt.field !== "block")); setFilterValues({}); })
        .finally(() => setIsLoadingFilters(false));
    } else { setDynamicSchema([]); setFilterValues({}); }
  }, [f.benType]);

  const handleDynamicFilterChange = (field, value) => {
    setFilterValues(prev => ({ ...prev, [field]: value }));
    if (field === "district") {
      setFilterValues(prev => ({ ...prev, block: "" })); setDynamicBlocks([]);
      if (value) {
        setIsLoadingBlocks(true);
        api.get("/beneficiaries/blocks", { params: { district: value } })
          .then(r => setDynamicBlocks(r.data.data || [])).finally(() => setIsLoadingBlocks(false));
      }
    }
  };

  const handleSearchBeneficiaries = async () => {
    if (!f.benType) return toast.error("Please select a beneficiary type first");
    setIsSearching(true); setHasSearchedBens(true);
    try {
      const r = await api.get("/beneficiaries", { params: { size: 200, type: f.benType, search: f.search || undefined, ...filterValues } });
      setBens(r.data.data.content || []);
    } catch (err) { toast.error("Failed to fetch beneficiaries"); setBens([]); } finally { setIsSearching(false); }
  };

  const toggle = (b) => {
    setSelectedBens(prev => prev.find(x => x.beneficiaryId === b.beneficiaryId) ? prev.filter(x => x.beneficiaryId !== b.beneficiaryId) : [...prev, b]);
  };

  // --- Save / Delete ---
  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this utilization?")) {
      try {
        await api.delete(`/utilizations/${id}`);
        toast.success("Utilization deleted");
        nav("/admin/utilizations");
      } catch (error) { toast.error("Failed to delete utilization"); }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!activePurposeId) return toast.error("Please select a sector / project / activity");
    if (!f.utilizedAmount) return toast.error("Enter a total utilization amount");

    if (totalAllocated > Number(f.utilizedAmount)) {
      return toast.error(`Mapped amounts (₹${totalAllocated}) exceed total amount (₹${f.utilizedAmount})`);
    }

    for (let s of fundingSources) {
      if (!s.allocatedAmount || Number(s.allocatedAmount) <= 0) return toast.error(`Enter a valid amount for Donation #${s.donationId}`);
    }

    setBusy(true);
    try {
      const payload = {
        donationPurposeId: Number(activePurposeId), 
        utilizationDate: f.utilizationDate,
        utilizedAmount: Number(f.utilizedAmount),
        utilizationDescription: f.utilizationDescription,
        fundingSources: fundingSources.map(s => ({ donationId: s.donationId, amount: Number(s.allocatedAmount) })),
        beneficiaryIds: selectedBens.map(b => b.beneficiaryId), 
      };
      
      await api.put(`/utilizations/${id}`, payload);
      toast.success("Utilization updated successfully");
      setIsEditing(false);
      loadData(); 
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update utilization");
    } finally { setBusy(false); }
  };

  if (err) return <Empty title="Not found" note={err} />;
  if (loading || !data) return <div className="text-muted-foreground p-8">Loading details...</div>;

  return (
    <div className="space-y-6">
      <Section 
        title={`Utilization #${data.utilizationId}`} 
        kicker={data.purposeName}
        right={
          <div className="flex items-center gap-3">
            {!isEditing ? (
              <>
                <Button variant="secondary" onClick={() => setIsEditing(true)}>
                  <Edit2 className="w-4 h-4" /> Edit
                </Button>
                <button onClick={handleDelete} className="text-red-600 hover:text-red-800 p-2"><Trash2 className="w-4 h-4" /></button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => { setIsEditing(false); initForm(data, purposes); }}>
                <X className="w-4 h-4" /> Cancel
              </Button>
            )}
          </div>
        }
      />

      {isEditing ? (
        <form onSubmit={handleUpdate} className="space-y-6" data-testid="utilization-edit-form">
          <div className="rounded-xl border border-emerald-900/10 bg-white p-6 shadow-sm space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select label="Sector" testid="fld-sector" value={f.sectorId} onChange={e=>setF(v=>({...v, sectorId: e.target.value, projectId:"", activityId:""}))}>
                <option value="">— select sector —</option>
                {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
              <Select label="Project" testid="fld-project" value={f.projectId} onChange={e=>setF(v=>({...v, projectId: e.target.value, activityId:""}))}>
                <option value="">— select project (optional) —</option>
                {projects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
              <Select label="Activity" testid="fld-activity" value={f.activityId} onChange={upd("activityId")}>
                <option value="">— select activity (optional) —</option>
                {activities.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>

              <Input label="Date" type="date" testid="fld-date" value={f.utilizationDate} onChange={upd("utilizationDate")} />
              <Input label="Total Amount (INR)" type="number" required min="1" step="0.01" testid="fld-amount" value={f.utilizedAmount} onChange={upd("utilizedAmount")} />
              
              <div className="lg:col-span-1">
                <Textarea label="Description" testid="fld-desc" rows={1} value={f.utilizationDescription} onChange={upd("utilizationDescription")} />
              </div>
            </div>
          </div>

          {/* MULTI-FUNDING SPLIT SECTION */}
          <div className="rounded-xl border border-emerald-900/10 bg-white p-6 shadow-sm space-y-5">
            <div>
              <div className="overline">Split Funding Details</div>
              <div className="font-display text-lg font-bold text-emerald-950">Map donations to this expense</div>
            </div>

            <div className="relative" ref={dropdownRef}>
              <div className="text-sm font-semibold text-emerald-950 mb-1.5">Add Specific Donation Source</div>
              <div className="relative">
                <Input 
                  placeholder="Search donor name, purpose, or ID..." 
                  value={donationSearchTerm}
                  onChange={(e) => { setDonationSearchTerm(e.target.value); setIsDonationDropdownOpen(true); }}
                  onClick={() => setIsDonationDropdownOpen(true)}
                />
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-emerald-900/40 pointer-events-none" />
              </div>

              {isDonationDropdownOpen && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto bg-white border border-emerald-900/10 rounded-lg shadow-xl">
                  {filteredDonations.length === 0 ? (
                    <div className="px-4 py-4 text-sm text-center text-muted-foreground">No matching donations available</div>
                  ) : (
                    filteredDonations.map(d => (
                      <button key={d.donationId} type="button" className="w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 border-b border-emerald-50 transition-colors" onClick={() => addFundingSource(d)}>
                        <div className="font-semibold text-emerald-950 flex justify-between">
                          <span>#{d.donationId} • {d.donorName}</span>
                          <span className="text-emerald-700 flex items-center gap-1"><Plus className="w-3 h-3"/> Select</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">Donated for: {d.purposeName || "General / ANY"}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {fundingSources.length > 0 && (
              <div className="border border-emerald-100 rounded-xl overflow-hidden mt-4">
                <table className="w-full text-sm text-left">
                  <thead className="bg-emerald-50/80 text-emerald-900 border-b border-emerald-100">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Donation</th>
                      <th className="px-4 py-3 font-semibold">Amount to Utilize (INR)</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-emerald-100">
                    {fundingSources.map(s => (
                      <tr key={s.donationId} className="bg-white">
                        <td className="px-4 py-3">
                          <div className="font-medium text-emerald-950">#{s.donationId} • {s.donorName}</div>
                          <div className="text-xs text-muted-foreground">{s.purposeName || "General"}</div>
                        </td>
                        <td className="px-4 py-3 w-48">
                          <Input type="number" min="1" placeholder="₹ 0"
                            value={s.allocatedAmount} onChange={e => updateSourceAmount(s.donationId, e.target.value)}
                            className="!py-1.5" />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" onClick={() => removeSource(s.donationId)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-lg text-sm font-medium">
              <AlertCircle className="w-5 h-5 text-blue-600 shrink-0" />
              <div className="flex-1">
                Event Total: <span className="font-bold">₹{f.utilizedAmount || 0}</span> • 
                Mapped from Donations: <span className="font-bold">₹{totalAllocated}</span>
              </div>
              {Number(f.utilizedAmount) > 0 && totalAllocated < Number(f.utilizedAmount) && (
                <div className="text-blue-700 bg-blue-100 px-2 py-1 rounded">Remaining ₹{Number(f.utilizedAmount) - totalAllocated} draws from General Pool</div>
              )}
            </div>
          </div>

          {/* BENEFICIARY SECTION */}
          <div className="rounded-xl border border-emerald-900/10 bg-white p-6 space-y-4 shadow-sm">
            <div>
              <div className="overline">Beneficiaries</div>
              <div className="font-display text-lg font-bold text-emerald-950">Select who this reached</div>
            </div>
              
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select testid="ben-type-filter" value={f.benType} onChange={e => { setF(v => ({...v, benType: e.target.value})); setBens([]); setHasSearchedBens(false); }}>
                <option value="">Select Type to search…</option>
                <option value="STUDENT">Student</option>
                <option value="SCHOOL">School</option>
                <option value="COMMUNITY">Community</option>
              </Select>
              <Input testid="ben-search" placeholder="Search by name…" value={f.search} onChange={upd("search")} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), handleSearchBeneficiaries())} />
            </div>

            {dynamicSchema.length > 0 && (
              <div className="p-4 bg-emerald-50/50 border border-emerald-100 rounded-lg space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                  <Filter className="w-4 h-4" /> {isLoadingFilters ? "Loading Filters..." : "Filter Hierarchy (Optional Selection)"}
                </div>
                {!isLoadingFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {dynamicSchema.map(filter => filter.inputType === "select" ? (
                      <Select key={filter.field} value={filterValues[filter.field] || ""} onChange={(e) => handleDynamicFilterChange(filter.field, e.target.value)}>
                        <option value="">All {filter.label}s</option>
                        {filter.options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </Select>
                    ) : (
                      <Input key={filter.field} placeholder={filter.label} type={filter.inputType || "text"} value={filterValues[filter.field] || ""} onChange={(e) => handleDynamicFilterChange(filter.field, e.target.value)} />
                    ))}
                    <Select value={filterValues["block"] || ""} onChange={(e) => handleDynamicFilterChange("block", e.target.value)} disabled={!filterValues["district"] || isLoadingBlocks}>
                      <option value="">{isLoadingBlocks ? "Loading blocks..." : "All Blocks"}</option>
                      {dynamicBlocks.map(block => <option key={block.value} value={block.value}>{block.label}</option>)}
                    </Select>
                  </div>
                )}
              </div>
            )}

            {f.benType && (
              <div className="flex justify-end pt-2 border-b border-emerald-900/10 pb-4">
                <Button type="button" onClick={handleSearchBeneficiaries} disabled={isSearching} className="flex items-center gap-2">
                  <Search className="w-4 h-4" /> {isSearching ? "Applying Filters..." : "Apply Filters & Fetch Data"}
                </Button>
              </div>
            )}

            {selectedBens.length > 0 && (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 flex flex-wrap gap-2">
                <div className="w-full text-xs font-semibold text-emerald-800 mb-1">Attached ({selectedBens.length}):</div>
                {selectedBens.map(sb => (
                  <div key={sb.beneficiaryId} className="flex items-center gap-1 bg-white border border-emerald-200 px-2 py-1 rounded-md text-xs font-medium text-emerald-900 shadow-sm">
                    <Badge tone={sb.type === "STUDENT" ? "brand" : sb.type === "SCHOOL" ? "indigo" : "warm"}>{sb.type?.charAt(0) || "B"}</Badge>
                    {sb.name}
                    <button type="button" onClick={() => setSelectedBens(prev => prev.filter(x => x.beneficiaryId !== sb.beneficiaryId))} className="text-emerald-600 hover:text-red-500 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-2" style={{ opacity: isSearching ? 0.5 : 1 }}>
              {hasSearchedBens ? (
                bens.length > 0 ? (
                  bens.map(b => {
                    const on = selectedBens.some(x => x.beneficiaryId === b.beneficiaryId);
                    return (
                      <button type="button" key={b.beneficiaryId} onClick={() => toggle(b)} className={`text-left rounded-lg border p-3 transition-colors ${on ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-emerald-900/10 bg-white hover:bg-emerald-50/50"}`}>
                        <div className="flex items-center justify-between">
                          <div className="mono text-[11px] text-emerald-800">{b.code || b.enr_id}</div>
                          <Badge tone={b.type === "STUDENT" ? "brand" : b.type === "SCHOOL" ? "indigo" : "warm"}>{b.type}</Badge>
                        </div>
                        <div className="mt-1 font-semibold text-emerald-950">{b.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{b.address || "—"}</div>
                      </button>
                    );
                  })
                ) : <div className="col-span-full py-8 text-center"><Empty title="No beneficiaries match your search" /></div>
              ) : <div className="col-span-full py-8 text-center text-sm text-muted-foreground">Select filters above and click Apply Filters to view results.</div>}
            </div>
          </div>
          <div className="pt-4 border-t border-emerald-900/10 flex justify-end">
            <Button type="submit" disabled={busy}><Save className="w-4 h-4 mr-2" /> Save Updates</Button>
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="Financials & Tracking">
            <Row k="Event Amount" v={fmtCurrency(data.amount, "INR")} mono strong />
            <Row k="Date" v={fmtDate(data.date)} />
            
            <div className="flex justify-between gap-3 mt-2 border-t border-emerald-900/10 pt-2">
              <span className="text-muted-foreground">Source Donation(s)</span>
              <div className="text-right flex flex-col gap-1">
                {data.fundingSources && data.fundingSources.length > 0 ? (
                  data.fundingSources.map(fs => (
                    <span key={fs.donationId}>
                      <Link to={`/admin/donations/${fs.donationId}`} className="font-mono text-emerald-800 font-semibold hover:underline">#{fs.donationId}</Link> 
                      <span className="text-xs text-muted-foreground ml-1">({fmtCurrency(fs.allocatedAmount, "INR")})</span>
                    </span>
                  ))
                ) : <span className="font-medium text-emerald-950">General Pool</span>}
              </div>
            </div>
            
            <div className="mt-2 border-t border-emerald-900/10 pt-2">
              <Row k="Description" v={data.description} />
            </div>
          </Card>

          <Card title="Impact & Beneficiaries">
            <div className="mb-3">
              <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wider block mb-1">Target Purpose</span>
              <span className="text-emerald-950 font-medium">{data.purposeName}</span>
            </div>
            <div className="border-t border-emerald-900/10 pt-3">
              <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wider block mb-2">Linked Beneficiaries ({data.beneficiaries?.length || 0})</span>
              {data.beneficiaries?.length > 0 ? (
                <div className="space-y-1">
                  {data.beneficiaries.map((b, i) => (
                    <div key={i} className="text-sm flex justify-between">
                      <span className="text-emerald-950">{b.name}</span>
                      <span className="font-mono text-muted-foreground text-xs">{b.code}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">No specific beneficiaries linked.</span>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }){ return <div className="rounded-xl border border-emerald-900/10 bg-white p-5"><div className="overline mb-3">{title}</div><div className="space-y-2 text-sm">{children}</div></div>; }
function Row({ k, v, mono, strong }){ return <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className={`${mono ? "font-mono" : ""} ${strong ? "font-bold text-emerald-900" : "font-medium text-emerald-950"} text-right`}>{v || "—"}</span></div>; }